import type { AuthenticatedUserContract, Locale } from "@chams-pay/contracts";
import { operationsMessages, translate } from "@chams-pay/contracts";
import { cn } from "@chams-pay/design-system/lib/utils";
import {
  CalendarRange,
  ChevronDown,
  CreditCard,
  Loader2,
  Lock,
  LogOut,
  Plus,
  RefreshCw,
  Settings2,
  TriangleAlert,
  UserPlus,
  WifiOff,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  casablancaDate,
  dashboardPeriodPresetBounds,
  formatStamp,
  initialOf,
  type DashboardPeriodPreset,
} from "../core/format.js";
import { deploymentProductName } from "../core/deployment.js";
import type { WorkspaceNavItem, WorkspaceScreen } from "../core/navigation.js";
import type {
  SyncDotTier,
  WorkspaceBannerKind,
  WorkspaceController,
} from "../core/use-workspace.js";
import { relativeAgeLabel } from "../core/use-workspace.js";
import { DatePicker } from "../ui/date-picker.js";
import { Bidi, Count, DateValue } from "../ui/figures.js";
import { showMobileFeedback, useAppBackClose } from "./app-feedback.js";
import { MobileDraggableSheet } from "./draggable-sheet.js";

type MessageKey = Parameters<typeof translate>[1];

const chipDot: Record<SyncDotTier, string> = {
  green: "bg-[#7de2a8] shadow-[0_0_0_3px_rgb(125_226_168/0.25)]",
  amber: "bg-[#f2c46b] shadow-[0_0_0_3px_rgb(242_196_107/0.25)]",
  red: "bg-white shadow-[0_0_0_3px_rgb(255_255_255/0.25)]",
};

function synchronizeWithFeedback(workspace: WorkspaceController): void {
  void workspace
    .sync()
    .then((synchronized) => synchronized && showMobileFeedback("sync-completed"));
}

export function MobileLanguageButton({
  locale,
  onChange,
}: {
  readonly locale: Locale;
  readonly onChange: () => void;
}) {
  const label = locale === "ar" ? "Français" : "العربية";
  return (
    <button
      type="button"
      onClick={onChange}
      aria-label={label}
      className="floating-language-switch chams-focusable grid size-11 shrink-0 place-items-center rounded-full bg-white/15 font-heading text-[11px] font-bold text-white"
    >
      {locale === "ar" ? "FR" : "ع"}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* App bar                                                             */
/* ------------------------------------------------------------------ */

export function MobileAppBar({
  locale,
  user,
  title,
  blocked,
  online,
  workspace,
  onOpenSync,
  onLocaleChange,
  segmented,
  leading,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract | null;
  readonly title: ReactNode;
  readonly blocked: boolean;
  readonly online: boolean;
  readonly workspace: WorkspaceController;
  readonly onOpenSync: () => void;
  readonly onLocaleChange: () => void;
  readonly segmented?: ReactNode;
  readonly leading?: ReactNode;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const displayName = user
    ? locale === "ar"
      ? user.displayNameAr
      : user.displayNameFr
    : deploymentProductName;
  return (
    <header
      data-blocked={blocked}
      className={cn(
        "shell-header sticky top-0 z-30 px-3 pb-3.5 pt-[max(0.625rem,env(safe-area-inset-top))] text-white shadow-[0_2px_8px_-2px_rgb(16_28_30/0.3)]",
        blocked ? "bg-destructive" : online ? "bg-primary" : "bg-[#5d6b6b]",
      )}
    >
      <div className="flex items-center gap-2.5">
        {leading}
        <div className="min-w-0 flex-1 font-heading text-[17px] font-bold">{title}</div>
        <MobileLanguageButton locale={locale} onChange={onLocaleChange} />
        <button
          type="button"
          onClick={onOpenSync}
          aria-label={t("profile.title")}
          className="mobile-profile-trigger chams-focusable relative grid size-11 shrink-0 place-items-center rounded-full bg-white/15 font-heading text-sm font-bold text-white"
        >
          <span aria-hidden="true" className="truncate">
            {initialOf(displayName)}
          </span>
          <span
            aria-hidden="true"
            className={cn(
              "absolute bottom-0.5 end-0.5 size-2.5 rounded-full border-2 border-current",
              chipDot[workspace.syncDot],
            )}
          />
        </button>
      </div>
      {segmented}
    </header>
  );
}

export function MobileSegmented({
  options,
  active,
  onSelect,
}: {
  readonly options: readonly { readonly id: string; readonly label: string }[];
  readonly active: string;
  readonly onSelect: (id: string) => void;
}) {
  return (
    <div className="mt-3.5 flex rounded-full bg-white/15 p-[3px]">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onSelect(option.id)}
          aria-pressed={option.id === active}
          className={cn(
            "chams-focusable h-[34px] flex-1 rounded-full font-heading text-xs transition-colors duration-fast",
            option.id === active
              ? "bg-white font-bold text-teal-700"
              : "font-semibold text-white/90",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function MobileDashboardPeriodPicker({
  locale,
  academicYear,
  from,
  to,
  onFrom,
  onTo,
}: {
  readonly locale: Locale;
  readonly academicYear: string | undefined;
  readonly from: string;
  readonly to: string;
  readonly onFrom: (value: string) => void;
  readonly onTo: (value: string) => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const text = operationsMessages(locale);
  const [open, setOpen] = useState(false);
  const today = casablancaDate();
  const choosePreset = (preset: DashboardPeriodPreset) => {
    const bounds = dashboardPeriodPresetBounds(preset, academicYear, today);
    onFrom(bounds.from);
    onTo(bounds.to);
  };

  return (
    <>
      <button
        type="button"
        dir="ltr"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="chams-focusable flex h-9 w-full items-center gap-2 rounded-full border border-input bg-card px-2 shadow-e1 transition-colors duration-fast active:bg-surface-2"
      >
        <CalendarRange aria-hidden="true" className="size-4 shrink-0 text-muted-2" />
        <span className="chams-figure min-w-0 flex-1 truncate text-center text-[11px] font-bold text-foreground">
          <DateValue value={from} /> – <DateValue value={to} />
        </span>
        <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted-2" />
      </button>

      <MobileDraggableSheet
        opened={open}
        onClose={() => setOpen(false)}
        onBackdropClick={() => setOpen(false)}
        className="mobile-workspace-sheet mobile-period-sheet w-full rounded-t-2xl"
      >
        <div className="flex flex-col gap-4 px-4 pb-6 pt-4">
          <div className="flex items-center gap-3">
            <strong className="font-heading text-base">{t("dash.activityPeriod")}</strong>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {(["week", "month", "academic-year"] as const).map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => choosePreset(preset)}
                className="chams-focusable min-h-11 rounded-xl border border-input bg-card px-2 font-heading text-[11px] font-bold text-muted-foreground"
              >
                {t(
                  preset === "week"
                    ? "dash.periodWeek"
                    : preset === "month"
                      ? "dash.periodMonth"
                      : "dash.periodAcademicYear",
                )}
              </button>
            ))}
          </div>
          <span className="chams-eyebrow">{t("dash.periodCustom")}</span>
          <DatePicker
            locale={locale}
            label={text.periodFrom}
            showLabel
            value={from}
            max={to}
            onChange={onFrom}
          />
          <DatePicker
            locale={locale}
            label={text.periodTo}
            showLabel
            value={to}
            min={from}
            max={today}
            onChange={onTo}
          />
        </div>
      </MobileDraggableSheet>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Bottom tab bar and floating action                                  */
/* ------------------------------------------------------------------ */

export function MobileTabbar({
  label,
  locale,
  items,
  activeScreen,
  instrumentCount,
  onSelect,
}: {
  readonly label: string;
  readonly locale: Locale;
  readonly items: readonly WorkspaceNavItem[];
  readonly activeScreen: WorkspaceScreen;
  readonly instrumentCount: number;
  readonly onSelect: (screen: WorkspaceScreen) => void;
}) {
  return (
    <nav
      aria-label={label}
      className="mobile-bottom-nav fixed bottom-0 start-0 z-40 w-full border-t border-border bg-white pb-[max(0.875rem,env(safe-area-inset-bottom))] shadow-[0_-2px_10px_-4px_rgb(16_28_30/0.12)]"
    >
      <div
        className="grid"
        style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
      >
        {items.map((item) => {
          const Glyph = item.icon;
          const active = activeScreen === item.id;
          const title = item.id === "instruments" ? translate(locale, "dash.tab3") : item.title;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item.id)}
              aria-current={active ? "page" : undefined}
              className="chams-focusable relative flex min-h-14 flex-col items-center justify-center gap-1.5 px-1"
            >
              <Glyph
                aria-hidden="true"
                strokeWidth={1.8}
                className={cn("size-5", active ? "text-primary" : "text-muted-2")}
              />
              {item.id === "instruments" && instrumentCount > 0 && (
                <span className="chams-figure absolute top-1.5 end-[22px] grid h-[17px] min-w-[17px] place-items-center rounded-full bg-warning px-1 text-[9.5px] font-bold text-white">
                  {String(instrumentCount)}
                </span>
              )}
              <span
                className={cn(
                  "max-w-full truncate font-heading text-[9.5px]",
                  active ? "font-bold text-primary" : "font-semibold text-muted-2",
                )}
              >
                {title}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export function MobileQuickActionsFab({
  locale,
  paymentDisabled,
  registrationDisabled,
  onTakePayment,
  onRegisterStudent,
}: {
  readonly locale: Locale;
  readonly paymentDisabled: boolean;
  readonly registrationDisabled: boolean;
  readonly onTakePayment: () => void;
  readonly onRegisterStudent: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const [open, setOpen] = useState(false);
  useAppBackClose(open, () => setOpen(false));
  const closeAndRun = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <>
      {open && (
        <button
          type="button"
          aria-label={t("common.close")}
          className="fixed inset-0 z-[110] bg-black/10"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
          }}
        />
      )}
      <div className="mobile-primary-action fixed bottom-24 end-[18px] z-[111] flex flex-col items-end gap-2.5">
        {open && (
          <>
            <button
              type="button"
              disabled={paymentDisabled}
              onClick={() => closeAndRun(onTakePayment)}
              className="chams-focusable inline-flex min-h-12 items-center gap-2.5 rounded-2xl bg-white px-4 font-heading text-xs font-bold text-foreground shadow-e2 disabled:cursor-not-allowed disabled:text-disabled"
            >
              <CreditCard aria-hidden="true" className="size-4 text-primary" />
              {t("sp.takePayment")}
            </button>
            <button
              type="button"
              disabled={registrationDisabled}
              onClick={() => closeAndRun(onRegisterStudent)}
              className="chams-focusable inline-flex min-h-12 items-center gap-2.5 rounded-2xl bg-white px-4 font-heading text-xs font-bold text-foreground shadow-e2 disabled:cursor-not-allowed disabled:text-disabled"
            >
              <UserPlus aria-hidden="true" className="size-4 text-primary" />
              {t("sp.registerStudent")}
            </button>
          </>
        )}
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${t("sp.takePayment")} / ${t("sp.registerStudent")}`}
          onClick={() => setOpen((current) => !current)}
          className="chams-focusable grid size-[62px] place-items-center rounded-2xl bg-primary text-white shadow-[0_10px_22px_-6px_rgb(42_138_148/0.7)]"
        >
          <Plus
            aria-hidden="true"
            className={cn("size-6 transition-transform duration-fast", open && "rotate-45")}
            strokeWidth={2}
          />
        </button>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Sync and account bottom sheet (screen 15)                           */
/* ------------------------------------------------------------------ */

export function MobileAccountSheet({
  locale,
  user,
  online,
  opened,
  workspace,
  onClose,
  onSettings,
  onLock,
  onLogout,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract | null;
  readonly online: boolean;
  readonly opened: boolean;
  readonly workspace: WorkspaceController;
  readonly onClose: () => void;
  readonly onSettings: () => void;
  readonly onLock: () => void;
  readonly onLogout: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const displayName = user
    ? locale === "ar"
      ? user.displayNameAr
      : user.displayNameFr
    : deploymentProductName;
  const role = t(
    user?.role === "system-administrator"
      ? "profile.systemAdministrator"
      : "profile.financialAdministrator",
  );

  return (
    <MobileDraggableSheet
      className="mobile-workspace-sheet profile-sheet w-full rounded-t-2xl"
      data-testid="mobile-profile-sheet"
      opened={opened}
      onClose={onClose}
      onBackdropClick={onClose}
    >
      <div className="flex flex-col gap-3.5 px-4 pb-6 pt-2.5">
        <div className="flex items-center gap-2.5">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary-soft font-heading text-sm font-bold text-primary-strong">
            {initialOf(displayName)}
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <strong className="truncate font-heading text-sm">{displayName}</strong>
            <span className="truncate text-[11px] text-muted-foreground">{role}</span>
          </span>
        </div>

        <div
          data-online={online}
          className="flex items-center gap-2.5 rounded-lg bg-sunken px-3.5 py-3 data-[online=true]:bg-success-soft"
        >
          <span
            aria-hidden="true"
            className={cn(
              "size-2 shrink-0 rounded-full",
              online ? "bg-success shadow-[0_0_0_3px_rgb(31_122_77/0.16)]" : "bg-muted-foreground",
            )}
          />
          <span
            className={cn(
              "font-heading text-xs font-bold",
              online ? "text-success" : "text-muted-foreground",
            )}
          >
            {t(online ? "dash.online" : "dash.offline")}
          </span>
          <span className="ms-auto flex flex-col items-end gap-0.5 text-end text-[11px] text-muted-foreground">
            <span>{t("dash.lastSync")}</span>
            <Bidi className="text-[10.5px]">
              {relativeAgeLabel(workspace.syncAgeMilliseconds, locale)}
            </Bidi>
          </span>
        </div>

        <div className="overflow-hidden rounded-lg bg-sunken">
          <div className="flex min-h-12 items-center justify-between bg-card px-3.5 text-xs text-muted-foreground">
            {t("dash.pendingPayments")}
            <Count value={workspace.pendingCount} className="text-sm font-bold text-foreground" />
          </div>
          <div className="mt-px flex min-h-12 items-center justify-between bg-card px-3.5 text-xs text-muted-foreground">
            {t("dash.localCopy")}
            <Bidi className="whitespace-nowrap text-xs">
              {`${workspace.replica.students.length} · ${formatStamp(
                workspace.replica.synchronizedAt,
                locale === "ar" ? "fr" : locale,
              )}`}
            </Bidi>
          </div>
        </div>

        <button
          type="button"
          disabled={!workspace.canSync}
          onClick={() => synchronizeWithFeedback(workspace)}
          className="chams-focusable flex min-h-[50px] items-center justify-center gap-2 rounded-xl bg-primary font-heading text-sm font-bold text-white disabled:bg-teal-200 disabled:text-teal-50"
        >
          <RefreshCw
            aria-hidden="true"
            className={cn("size-4", workspace.syncing && "animate-spin")}
          />
          {t("dash.syncNow")}
        </button>

        {workspace.readinessTier === "warning" && (
          <p className="rounded-lg bg-warning-soft px-3.5 py-3 text-[11.5px] leading-relaxed text-warning">
            {t("dash.readinessWarn")}
          </p>
        )}

        <div className="h-px bg-border" />

        <div className="flex flex-col gap-0.5">
          <button
            type="button"
            onClick={() => {
              onClose();
              onSettings();
            }}
            className="chams-focusable flex min-h-11 items-center gap-2.5 rounded-lg px-2.5 font-heading text-[12.5px] font-semibold text-muted-foreground"
          >
            <Settings2 aria-hidden="true" className="size-4" />
            {t("dash.nav5")}
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
              onLock();
            }}
            className="chams-focusable flex min-h-11 items-center gap-2.5 rounded-lg px-2.5 font-heading text-[12.5px] font-semibold text-muted-foreground"
          >
            <Lock aria-hidden="true" className="size-4" />
            {t("dash.lock")}
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
              onLogout();
            }}
            className="chams-focusable flex min-h-11 items-center gap-2.5 rounded-lg px-2.5 font-heading text-[12.5px] font-semibold text-destructive"
          >
            <LogOut aria-hidden="true" className="size-4" />
            {t("dash.signOut")}
          </button>
        </div>
      </div>
    </MobileDraggableSheet>
  );
}

/* ------------------------------------------------------------------ */
/* Single-banner slot                                                  */
/* ------------------------------------------------------------------ */

export function MobileWorkspaceBanner({
  locale,
  kind,
  workspace,
  updateActivating,
  onActivateUpdate,
}: {
  readonly locale: Locale;
  readonly kind: WorkspaceBannerKind;
  readonly workspace: WorkspaceController;
  readonly updateActivating: boolean;
  readonly onActivateUpdate: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  if (!kind) return null;

  if (kind === "capture-blocked") {
    const storageBlocked = workspace.captureBlockReason === "storage";
    const clockBlocked = workspace.captureBlockReason === "clock";
    const titleKey = storageBlocked
      ? "dash.storageBlockedTitle"
      : clockBlocked
        ? "dash.clockBlockedTitle"
        : "dash.blockedTitle";
    const bodyKey = storageBlocked
      ? "dash.storageBlockedBody"
      : clockBlocked
        ? "dash.clockBlockedBody"
        : "dash.blockedBody";
    return (
      <div
        role="alert"
        className="workspace-status-banner mx-3.5 mt-3.5 flex flex-col gap-3 rounded-2xl bg-destructive p-4 shadow-[0_8px_20px_-8px_rgb(179_38_30/0.55)]"
      >
        <span className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="grid size-6 shrink-0 place-items-center rounded-full bg-white/20 font-heading text-sm font-extrabold text-white"
          >
            !
          </span>
          <strong className="font-heading text-[14.5px] leading-snug text-white">
            {t(titleKey)}
          </strong>
        </span>
        <span className="text-xs leading-relaxed text-white/90">{t(bodyKey)}</span>
        <button
          type="button"
          disabled={!workspace.canSync}
          onClick={() => synchronizeWithFeedback(workspace)}
          className="chams-focusable min-h-[50px] rounded-xl bg-white font-heading text-sm font-bold text-destructive disabled:opacity-70"
        >
          {t("dash.syncNow")}
        </button>
      </div>
    );
  }

  if (kind === "offline") {
    return (
      <p
        role="status"
        className="workspace-status-banner flex items-center gap-2 bg-[#5d6b6b] px-4 py-2.5 text-xs text-white"
      >
        <WifiOff aria-hidden="true" className="size-4 shrink-0" />
        {t("ops.bannerOffline")}
        <span className="ms-auto text-white/80">
          <Bidi>{relativeAgeLabel(workspace.syncAgeMilliseconds, locale)}</Bidi>
        </span>
      </p>
    );
  }

  if (kind === "sync-failed") {
    return (
      <div
        role="alert"
        className="workspace-status-banner flex items-center gap-2 bg-destructive-soft px-4 py-2.5 text-xs text-destructive"
      >
        <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />
        <span className="flex flex-col gap-0.5">
          <strong className="font-heading">{t("ops.bannerSyncFailed")}</strong>
          <span className="text-[11px]">{t("ops.bannerSyncFailedBody")}</span>
        </span>
        <button
          type="button"
          disabled={!workspace.canSync}
          onClick={() => synchronizeWithFeedback(workspace)}
          className="chams-focusable ms-auto min-h-9 shrink-0 rounded-md border border-destructive-border bg-card px-3 font-heading text-[11.5px] font-bold text-destructive"
        >
          {t("ops.retry")}
        </button>
      </div>
    );
  }

  if (kind === "update-after-sync") {
    return (
      <p
        role="status"
        className="workspace-status-banner flex items-center gap-2 bg-info-soft px-4 py-2.5 text-xs text-info"
      >
        <Loader2 aria-hidden="true" className="size-4 shrink-0 animate-spin" />
        <span className="flex flex-col gap-0.5">
          <strong className="font-heading">{t("ops.bannerUpdateAfterSync")}</strong>
          <span className="text-[11px]">{t("ops.bannerUpdateAfterSyncBody")}</span>
        </span>
        <Count value={workspace.pendingCount} className="ms-auto font-bold" />
      </p>
    );
  }

  return (
    <div
      role="status"
      className="workspace-status-banner flex items-center gap-2 bg-warning-soft px-4 py-2.5 text-xs text-warning"
    >
      <span className="flex flex-col gap-0.5">
        <strong className="font-heading">{t("ops.bannerUpdate")}</strong>
        <span className="text-[11px]">{t("ops.bannerUpdateBody")}</span>
      </span>
      <button
        type="button"
        disabled={updateActivating}
        onClick={onActivateUpdate}
        className="chams-focusable ms-auto inline-flex min-h-9 shrink-0 items-center gap-2 rounded-md border border-warning-border bg-card px-3 font-heading text-[11.5px] font-bold text-warning disabled:cursor-wait disabled:opacity-70"
      >
        {updateActivating && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
        {t("ops.restartNow")}
      </button>
    </div>
  );
}

/** Bottom padding that clears both the fixed tab bar and the floating action button. */
export function TabbarSpacer({ children }: { readonly children?: ReactNode }) {
  return <div className="h-44 pb-safe">{children}</div>;
}
